/**
 * Settings Service
 *
 * Provides unified interface for Settings, Admin Users, and Integrations
 * Settings methods use the new Settings API with automatic transformation
 */

import api from './api';
import settingsApiClient from './settingsApiClient';
import { transformNewToOld, transformOldToNew } from '../utils/settingsTransformers';
import settingsLogger from '../utils/settingsLogger';

const settingsService = {
  // ==================== SETTINGS METHODS ====================

  /**
   * Get all settings
   * @returns {Promise<Object>} Settings in frontend format { section: { key: value } }
   */
  getSettings: async () => {
    const stopTimer = settingsLogger.startTimer('getSettings');

    try {
      settingsLogger.logApiCall('GET', '/api/settings/grouped');

      // Fetch from new API
      const groupedSettings = await settingsApiClient.getSettingsGrouped();

      // Transform to frontend format
      const frontendSettings = transformNewToOld(groupedSettings);

      settingsLogger.logApiResponse('GET', '/api/settings/grouped', frontendSettings);
      stopTimer({ settingsCount: Object.keys(frontendSettings).length });

      return { data: { data: frontendSettings } };
    } catch (error) {
      settingsLogger.logError('getSettings', error);
      throw error;
    }
  },

  /**
   * Update all settings
   * @param {Object} data - Settings in frontend format { section: { key: value } }
   * @returns {Promise<Object>} Updated settings
   */
  updateSettings: async (data) => {
    const stopTimer = settingsLogger.startTimer('updateSettings');

    try {
      settingsLogger.logApiCall('PUT', '/api/settings (batch)', data);

      // Transform to new API format
      const apiPayload = transformOldToNew(data);

      settingsLogger.logTransformation('old', 'new', data, apiPayload);

      // Batch update all settings
      await settingsApiClient.updateSettingsBatch(apiPayload);

      // Fetch updated settings to return
      const groupedSettings = await settingsApiClient.getSettingsGrouped();
      const frontendSettings = transformNewToOld(groupedSettings);

      settingsLogger.logApiResponse('PUT', '/api/settings (batch)', frontendSettings);
      stopTimer({ settingsUpdated: apiPayload.length });

      return { data: { data: frontendSettings } };
    } catch (error) {
      settingsLogger.logError('updateSettings', error, { data });
      throw error;
    }
  },

  /**
   * Update specific section
   * @param {string} section - Section name (e.g., 'payment', 'general')
   * @param {Object} data - Complete settings object (we'll extract the section)
   * @returns {Promise<Object>} Updated settings
   */
  updateSettingsSection: async (section, data) => {
    const stopTimer = settingsLogger.startTimer(`updateSettingsSection:${section}`);

    try {
      settingsLogger.logApiCall('PUT', `/api/settings/section/${section}`, data);

      // Extract the section data
      const sectionData = data[section];

      if (!sectionData) {
        throw new Error(`Section "${section}" not found in data`);
      }

      // Transform only this section to new API format
      const apiPayload = transformOldToNew({ [section]: sectionData });

      settingsLogger.logTransformation('old', 'new', { [section]: sectionData }, apiPayload);

      // Batch update section settings
      await settingsApiClient.updateSettingsBatch(apiPayload);

      // Fetch updated settings to return
      const groupedSettings = await settingsApiClient.getSettingsGrouped();
      const frontendSettings = transformNewToOld(groupedSettings);

      settingsLogger.logApiResponse('PUT', `/api/settings/section/${section}`, frontendSettings);
      stopTimer({ settingsUpdated: apiPayload.length });

      return { data: { data: frontendSettings } };
    } catch (error) {
      settingsLogger.logError('updateSettingsSection', error, { section, data });
      throw error;
    }
  },

  /**
   * Get settings by section (new method - bonus utility)
   * @param {string} section - Section name
   * @returns {Promise<Object>} Section settings in frontend format
   */
  getSettingsBySection: async (section) => {
    const stopTimer = settingsLogger.startTimer(`getSettingsBySection:${section}`);

    try {
      settingsLogger.logApiCall('GET', `/api/settings/section/${section}`);

      const sectionSettings = await settingsApiClient.getSettingsBySection(section);

      // Transform to frontend format
      const frontendSettings = transformNewToOld({ [section]: sectionSettings });

      settingsLogger.logApiResponse('GET', `/api/settings/section/${section}`, frontendSettings);
      stopTimer();

      return { data: frontendSettings[section] || {} };
    } catch (error) {
      settingsLogger.logError('getSettingsBySection', error, { section });
      throw error;
    }
  },

  /**
   * Get single setting value (new method - bonus utility)
   * @param {string} section - Section name
   * @param {string} key - Setting key
   * @returns {Promise<any>} Setting value (typed)
   */
  getSetting: async (section, key) => {
    try {
      const setting = await settingsApiClient.getSetting(section, key);

      if (!setting) {
        return { data: null };
      }

      // Parse the value to correct type
      const frontendFormat = transformNewToOld({ [section]: [setting] });
      const value = frontendFormat[section]?.[key];

      return { data: value };
    } catch (error) {
      settingsLogger.logError('getSetting', error, { section, key });
      throw error;
    }
  },

  /**
   * Update single setting (new method - bonus utility)
   * @param {string} section - Section name
   * @param {string} key - Setting key
   * @param {any} value - Setting value (any type)
   * @returns {Promise<Object>} Updated setting
   */
  updateSetting: async (section, key, value) => {
    const stopTimer = settingsLogger.startTimer(`updateSetting:${section}.${key}`);

    try {
      settingsLogger.logApiCall('PUT', `/api/settings/${section}/${key}`, { value });

      await settingsApiClient.updateSetting(section, key, value);

      settingsLogger.logApiResponse('PUT', `/api/settings/${section}/${key}`, { success: true });
      stopTimer();

      return { data: { section, key, value } };
    } catch (error) {
      settingsLogger.logError('updateSetting', error, { section, key, value });
      throw error;
    }
  },

  // ==================== ADMIN USERS METHODS ====================
  // These methods are unchanged - they use a different API

  /**
   * Get all admin users - filter by role
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Admin users list
   */
  getAdminUsers: (params = {}) => {
    return api.get('/api/users', { params: { ...params, role: 'ADMIN' } });
  },

  /**
   * Get single admin user
   * @param {string} id - User ID
   * @returns {Promise<Object>} Admin user
   */
  getAdminUser: (id) => {
    return api.get(`/api/users/${id}`);
  },

  /**
   * Create admin user - use registration endpoint
   * @param {Object} data - User data
   * @returns {Promise<Object>} Created user
   */
  createAdminUser: (data) => {
    return api.post('/api/auth/register', data);
  },

  /**
   * Update admin user
   * @param {string} id - User ID
   * @param {Object} data - User data
   * @returns {Promise<Object>} Updated user
   */
  updateAdminUser: (id, data) => {
    return api.put(`/api/users/${id}`, data);
  },

  /**
   * Delete admin user
   * @param {string} id - User ID
   * @returns {Promise<void>}
   */
  deleteAdminUser: (id) => {
    return api.delete(`/api/users/${id}`);
  },

  // ==================== INTEGRATIONS METHODS ====================
  // These methods are unchanged - they use a different API

  /**
   * Get all integrations
   * @returns {Promise<Object>} Integrations list
   */
  getIntegrations: () => {
    return api.get('/api/integrations');
  },

  /**
   * Get single integration
   * @param {string} id - Integration ID
   * @returns {Promise<Object>} Integration
   */
  getIntegration: (id) => {
    return api.get(`/api/integrations/${id}`);
  },

  /**
   * Create integration
   * @param {Object} data - Integration data
   * @returns {Promise<Object>} Created integration
   */
  createIntegration: (data) => {
    return api.post('/api/integrations', data);
  },

  /**
   * Update integration
   * @param {string} id - Integration ID
   * @param {Object} data - Integration data
   * @returns {Promise<Object>} Updated integration
   */
  updateIntegration: (id, data) => {
    return api.put(`/api/integrations/${id}`, data);
  },

  /**
   * Test integration
   * @param {string} id - Integration ID
   * @returns {Promise<Object>} Test result
   */
  testIntegration: (id) => {
    return api.post(`/api/integrations/${id}/test`);
  },
};

export default settingsService;
